import classNames from 'classnames'
import { Link } from 'gatsby-link'
import React, { FC, useContext } from 'react'
import { HeaderDataContext } from '../../context/HeaderContext'
import { useTranslations } from '../../hooks/useTranslations'
import {
  isNullOrWhiteSpace,
  sanitizeHtmlAndReplaceNewlines,
} from '../../utilities/string'
import { Button } from '../Button'
import { Grid } from '../Grid'
import { Image, ImageProps } from '../Image'

export interface CareersTextItem {
  title: string
  text: string
  linkUrl: string
  linkText?: string
}

export interface CareersProps {
  image?: ImageProps
  items: CareersTextItem[]
  pageHasStickyMenu?: boolean
  title: string
}

export const Careers: FC<CareersProps> = ({ image, items, pageHasStickyMenu, title }) => {
  const translations = useTranslations()
  const firstItem = items?.find(_ => true)

  let topOffset = useContext(HeaderDataContext)

  if (pageHasStickyMenu) {
    topOffset += 75
  }

  return (
    <Grid>
      <div
        className={classNames(
          'col-span-full flex flex-col',
          'tb:grid tb:grid-cols-tb tb:auto-rows-fr-autoheader tb:gap-x-30 tb:place-content-center',
          'sm:grid-cols-sm',
          'md:grid-cols-md',
          'lg:grid-cols-lg',
          'xl:grid-cols-xl',
          'xxl:grid-cols-xxl'
        )}
      >
        <h2
          className={classNames(
            'w-330 mx-auto mb-30 font-h2 text-h2 text-primary',
            'tb:col-start-1 tb:col-span-2 tb:w-auto tb:ml-30 tb:-mr-30',
            'sm:col-start-2 sm:col-span-1 sm:ml-0 sm:-mr-75',
            'md:col-start-1 md:col-span-1 md:-mr-30',
            'lg:col-start-2 lg:col-span-1',
            'xxl:col-start-3 xxl:col-span-1 xxl:mr-30'
          )}
        >
          {/* No proper link text provided? Turn title into a link, 
            for SEO purposes (project decision) */}
          {firstItem &&
          !isNullOrWhiteSpace(firstItem.linkUrl) &&
          isNullOrWhiteSpace(firstItem.linkText) ? (
            <Link to={firstItem.linkUrl}>{title}</Link>
          ) : (
            title
          )}
        </h2>

        {!!items?.length &&
          items.map((item, i) => (
            <div
              key={i}
              className={classNames(
                'grid grid-cols-mobile gap-x-30 mx-auto',
                i === 0 ? 'order-none' : 'order-last',
                'tb:col-start-1 tb:col-span-2 tb:flex tb:flex-col tb:gap-x-0 tb:ml-30',
                'sm:col-start-2 sm:col-span-2 sm:ml-0',
                'md:col-start-1 md:col-span-2 md:-mr-30',
                'lg:col-start-2 lg:col-span-2',
                'xxl:col-start-3 xxl:col-span-3 xxl:mr-30'
              )}
            >
              <article
                className={classNames(
                  'col-span-full flex flex-col items-start tb:sticky',
                  {
                    'tb:mb-150': i < items.length - 1,
                  }
                )}
                style={{
                  top: `${topOffset}px`,
                }}
              >
                <h3
                  className={classNames(
                    'mb-15 font-h3 text-h3 text-secondary-dark',
                    'sm:mb-30',
                    'xl:mb-20'
                  )}
                >
                  {item.title}
                </h3>

                <p
                  className={classNames(
                    'mr-30 mb-30 font-t-light text-t-300 text-secondary-darker',
                    'sm:text-t-400',
                    'md:mr-0 md:mb-45 md:text-t-500',
                    'xxl:text-t-600'
                  )}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtmlAndReplaceNewlines(item.text),
                  }}
                />

                {item.linkUrl && (
                  <Button
                    additionalClassName="mb-30 place-self-center tb:place-self-start"
                    url={item.linkUrl}
                  >
                    {item.linkText?.length ? item.linkText : translations.more}
                  </Button>
                )}
              </article>
            </div>
          ))}

        {!!image && (
          <Image
            {...image}
            applyLqip
            className={classNames(
              'order-1 w-full mb-30 tb:sticky top-90',
              'tb:col-start-3 tb:col-span-2',
              'sm:col-start-4 sm:col-span-3',
              'md:col-start-4 md:col-span-3 md:ml-20',
              'lg:col-start-5 lg:col-span-4',
              'xl:col-start-6 xl:col-span-5 xl:ml-0',
              'xxl:col-start-8 xxl:col-span-7'
            )}
            loading="lazy"
            imgClassName="w-full"
            pictureClassName="tb:block tb:w-screen-1/2 tb:max-w-[1280px]"
          />
        )}
      </div>
    </Grid>
  )
}
